import { useEffect, useRef } from 'react';

const problems = [
  {
    icon: 'ri-user-unfollow-line',
    color: 'text-red-400',
    bg: 'bg-red-900/20',
    border: 'border-red-500/20',
    title: 'Ziyaretçi Girer Çıkar',
    description: 'İnsanlar sitenizi ziyaret ediyor ama hiç iletişime geçmeden ayrılıyor. Siteniz yalnızca bir vitrin gibi duruyor.',
  },
  {
    icon: 'ri-chat-off-line',
    color: 'text-orange-400',
    bg: 'bg-orange-900/20',
    border: 'border-orange-500/20',
    title: 'İletişime Geçilmiyor',
    description: 'Form bulunamıyor, WhatsApp yok, telefon numarası arka planda gizli. Potansiyel müşteri bir adım atmıyor.',
  },
  {
    icon: 'ri-funds-line',
    color: 'text-yellow-400',
    bg: 'bg-yellow-900/20',
    border: 'border-yellow-500/20',
    title: 'Reklam Bütçesi Boşa Gider',
    description: 'Google veya Meta\'ya para veriyorsunuz ama dönüşüm yok. Trafik geliyor, müşteri gelmiyor. Paramız gidiyor.',
  },
];

export default function ProblemSection() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.problem-card').forEach((card, i) => {
              setTimeout(() => {
                (card as HTMLElement).style.opacity = '1';
                (card as HTMLElement).style.transform = 'translateY(0)';
              }, i * 150);
            });
          }
        });
      },
      { threshold: 0.2 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="problem" className="py-24 md:py-32 relative overflow-hidden" style={{ background: '#0d0d15' }}>
      {/* Background accent */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-red-500/30 to-transparent" />
      <div className="absolute inset-0 opacity-[0.02]" style={{
        backgroundImage: 'radial-gradient(circle at 50% 50%, #ff4444 0%, transparent 60%)',
      }} />

      <div className="max-w-6xl mx-auto px-4 md:px-8" ref={ref}>
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-red-900/20 border border-red-500/20 px-4 py-2 rounded-full mb-5">
            <i className="ri-error-warning-line text-red-400 text-sm" />
            <span className="text-red-400 text-sm font-semibold">Gerçek Sorun</span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-white mb-5 tracking-tight max-w-3xl mx-auto">
            Çoğu Web Sitesi Sadece{' '}
            <span className="text-gold-gradient">Görünür… Ama Satış Yapmaz</span>
          </h2>

          {/* Punch line */}
          <div className="flex items-center justify-center gap-3 mt-2 mb-6 px-2">
            <span className="hidden sm:block w-8 h-px bg-red-500/40 flex-shrink-0" />
            <p className="text-base md:text-lg text-gray-300 italic max-w-xl leading-relaxed text-center">
              "Siteniz çalışıyor gibi görünür…{' '}
              <span className="text-red-400 font-bold not-italic">ama size para kazandırmaz.</span>"
            </p>
            <span className="hidden sm:block w-8 h-px bg-red-500/40 flex-shrink-0" />
          </div>

          <p className="text-gray-500 text-base max-w-xl mx-auto">
            Siteniz yayında olabilir, ama bu tek başına yeterli değil. İşte gerçek problem:
          </p>
        </div>

        {/* Problem Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
          {problems.map((problem) => (
            <div
              key={problem.title}
              className={`problem-card glass rounded-2xl p-7 border ${problem.border} card-hover`}
              style={{ opacity: 0, transform: 'translateY(30px)', transition: 'all 0.5s ease' }}
            >
              <div className={`w-14 h-14 flex items-center justify-center rounded-2xl ${problem.bg} border ${problem.border} mb-5`}>
                <i className={`${problem.icon} text-2xl ${problem.color}`} />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">{problem.title}</h3>
              <p className="text-gray-400 text-sm leading-relaxed">{problem.description}</p>
            </div>
          ))}
        </div>

        {/* Provocation question — the gut-punch */}
        <div className="mb-10 max-w-2xl mx-auto">
          <div
            className="block rounded-2xl px-6 md:px-8 py-5 border border-red-500/25 relative overflow-hidden"
            style={{ background: 'rgba(220,38,38,0.07)', backdropFilter: 'blur(12px)' }}
          >
            <div className="absolute left-0 top-0 bottom-0 w-1 rounded-l-2xl" style={{ background: 'linear-gradient(to bottom, #ef4444, #f97316)' }} />
            <p className="text-xl md:text-2xl font-extrabold text-white leading-snug text-center">
              Peki siz bu ay sitenizden{' '}
              <span className="relative inline-block">
                <span className="text-red-400">kaç müşteri kazandınız?</span>
                <span className="absolute -bottom-1 left-0 right-0 h-px bg-red-400/40" />
              </span>
            </p>
            <p className="text-gray-500 text-sm mt-2 font-medium text-center">
              Cevabınız "bilmiyorum" ya da "çok az" ise — sorun tam da burada.
            </p>
          </div>
        </div>

        {/* Bold bottom statement */}
        <div className="glass border border-white/5 rounded-2xl p-8 text-center max-w-3xl mx-auto">
          <p className="text-2xl md:text-3xl font-extrabold text-white leading-snug">
            Yüzlerce kişi görmüş olabilir.{' '}
            <span className="text-gold-gradient">Ama kaçı müşteri oldu?</span>
          </p>
          <p className="text-gray-400 mt-4">
            Eğer cevabınız "bilmiyorum" ise, sorun tasarımda değil — sistemde.
          </p>
        </div>
      </div>
    </section>
  );
}
